import API from "../services/api";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import { Link, useLocation, useParams } from "react-router-dom";
import formOptions from "../data/applyFormOptions.json";
import "react-datepicker/dist/react-datepicker.css";
import "./ApplyPage.css";

const EDUCATION_OPTIONS = {
  isHigherEducation: ["Yes", "No"],
  qualification: formOptions.qualification,
  specialization: formOptions.specialization,
  educationType: ["Full Time", "Part Time", " Long Distance"]
};

const SKILL_OPTIONS = formOptions.skills;

const INDIA_STATES_AND_UTS = formOptions.indiaStatesAndUts;

const createEducationEntry = () => ({
  isHigherEducation: "",
  qualification: "",
  specialization: "",
  educationType: "",
  collegeInstituteName: "",
  universityName: "",
  customUniversityName: "",
  educationFromDate: "",
  educationEndDate: ""
});

const SearchableDropdown = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = "No Selection"
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const hasSelectedOption = options.includes(value);
  const filteredOptions = hasSelectedOption
    ? options
    : options.filter((option) => option.toLowerCase().includes(value.toLowerCase()));

  return (
    <div className="apply-searchable-select">
      <div className={`apply-searchable-control${isOpen ? " open" : ""}`}>
        <input
          id={id}
          name={name}
          value={value}
          placeholder={placeholder}
          autoComplete="off"
          onFocus={() => setIsOpen(true)}
          onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
          onChange={onChange}
        />
        <span className="apply-searchable-arrow" aria-hidden="true">
          v
        </span>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="apply-searchable-menu">
          {filteredOptions.map((option) => (
            <button
              key={option}
              type="button"
              className={`apply-searchable-option${option === value ? " active" : ""}`}
              onMouseDown={() => {
                onChange({ target: { name, value: option } });
                setIsOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const MultiSelectDropdown = ({
  id,
  name,
  value,
  onChange,
  options,
  placeholder = "Type or select skills"
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const normalizedValue = Array.isArray(value) ? value : [];
  const filteredOptions = options.filter((option) =>
    option.toLowerCase().includes(searchValue.toLowerCase())
  );

  const updateSelection = (nextValues) => {
    onChange({ target: { name, value: nextValues } });
  };

  const addSkill = (skill) => {
    if (normalizedValue.includes(skill)) {
      return;
    }

    updateSelection([...normalizedValue, skill]);
    setSearchValue("");
  };

  const removeSkill = (skill) => {
    updateSelection(normalizedValue.filter((item) => item !== skill));
  };

  return (
    <div className="apply-searchable-select">
      <div className={`apply-searchable-control apply-multiselect-control${isOpen ? " open" : ""}`}>
        <div className="apply-multiselect-values">
          {normalizedValue.map((skill) => (
            <span key={skill} className="apply-multiselect-chip">
              {skill}
              <button
                type="button"
                className="apply-multiselect-chip-remove"
                onClick={() => removeSkill(skill)}
                aria-label={`Remove ${skill}`}
              >
                x
              </button>
            </span>
          ))}
          <input
            id={id}
            name={name}
            value={searchValue}
            placeholder={normalizedValue.length ? "" : placeholder}
            autoComplete="off"
            onFocus={() => setIsOpen(true)}
            onBlur={() => window.setTimeout(() => setIsOpen(false), 120)}
            onChange={(event) => setSearchValue(event.target.value)}
          />
        </div>
        <span className="apply-searchable-arrow" aria-hidden="true">
          v
        </span>
      </div>

      {isOpen && filteredOptions.length > 0 && (
        <div className="apply-searchable-menu">
          {filteredOptions.map((option) => {
            const isSelected = normalizedValue.includes(option);

            return (
              <button
                key={option}
                type="button"
                className={`apply-searchable-option${isSelected ? " active" : ""}`}
                onMouseDown={() => {
                  if (!isSelected) {
                    addSkill(option);
                  }
                }}
              >
                {option}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const RequiredMark = () => (
  <span aria-hidden="true" style={{ color: "#d62828", marginRight: "4px" }}>
    *
  </span>
);
const createCertificationEntry = () => ({
  certificationName: "",
  institution: "",
  effectiveDate: "",
  expirationDate: ""
});

const parseDateValue = (value) => {
  if (!value) {
    return null;
  }

  const [year, month, day] = value.split("-").map(Number);

  if (!year || !month || !day) {
    return null;
  }

  return new Date(year, month - 1, day);
};

const formatDateValue = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return "";
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

const normalizeUniversityName = (entry) =>
  entry.universityName === "Others"
    ? entry.customUniversityName.trim()
    : entry.universityName;

const displayValue = (value) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "Not provided";
  }

  if (value instanceof File) {
    return value.name;
  }

  return String(value || "").trim() || "Not provided";
};

const createInitialFormData = () => ({
  title: "",
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  secondaryPhone: "",
  totalExperience: "",
  isHigherEducation: "",
  qualification: "",
  specialization: "",
  educationType: "",
  collegeInstituteName: "",
  universityName: "",
  educationFromDate: "",
  educationEndDate: "",
  currentCountry: "",
  customCountry: "",
  currentAddressLine1: "",
  currentAddressLine2: "",
  currentCity: "",
  currentState: "",
  currentZipCode: "",
  fullName: "",
  email: "",
  phone: "",
  employeeId: "",
  referred: "",
  skills: [],
  otherSkillSet: "",
  linkedin: "",
  portfolio: "",
  resume: null
});

const ApplyPage = () => {
  const { jobId } = useParams();
  const location = useLocation();

  const backJobPath = location.state?.from || `/candidate/candidatecareer/${jobId}`;

  const [formData, setFormData] = useState(createInitialFormData);

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [resumeSuccess, setResumeSuccess] = useState("");
  const [status, setStatus] = useState("");
  const [openSection, setOpenSection] = useState("my-documents");
  const [submitMessage, setSubmitMessage] = useState("");
  const [validationPopupMessage, setValidationPopupMessage] = useState("");
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showReferralInfo, setShowReferralInfo] = useState(false);
  const [candidateEmail, setCandidateEmail] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [educationEntries, setEducationEntries] = useState([createEducationEntry()]);
  const [certificationEntries, setCertificationEntries] = useState([createCertificationEntry()]);

  useEffect(() => {
    const fetchCandidateProfile = async () => {
      try {
        const res = await API.get("/client/candidate/profile");
        const email = res.data.data?.email || "";

        setCandidateEmail(email);
        setFormData((prev) => ({ ...prev, email }));
      } catch (error) {
        console.error("Fetch candidate profile error:", error);
      }
    };

    fetchCandidateProfile();
  }, []);

  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "resume") {
      const file = files[0];

      if (file) {
        if (file.type !== "application/pdf") {
          setErrors((prev) => ({ ...prev, resume: "Only PDF file allowed" }));
          setResumeSuccess("");
          setFormData((prev) => ({ ...prev, resume: null }));
          return;
        }

        if (file.size > 5 * 1024 * 1024) {
          setErrors((prev) => ({ ...prev, resume: "Resume must be less than 5MB" }));
          setResumeSuccess("");
          setFormData((prev) => ({ ...prev, resume: null }));
          return;
        }

        setErrors((prev) => ({ ...prev, resume: "" }));
        setResumeSuccess("Resume uploaded successfully");
        setFormData((prev) => ({ ...prev, resume: file }));
      }

      return;
    }

    if (name === "phone") {
      const digits = value.replace(/\D/g, "");

      if (digits.length <= 10) {
        setFormData((prev) => ({ ...prev, phone: digits }));
        setErrors((prev) => ({ ...prev, phone: "" }));
      }

      return;
    }

    if (name === "secondaryPhone") {
      const digits = value.replace(/\D/g, "");

      if (digits.length <= 10) {
        setFormData((prev) => ({ ...prev, secondaryPhone: digits }));
      }

      return;
    }

    if (name === "employeeId") {
      setFormData((prev) => ({ ...prev, employeeId: value, referred: value.trim() ? "true" : "" }));
      setErrors((prev) => ({ ...prev, employeeId: "", referred: "" }));
      return;
    }

    if (name === "currentZipCode" || name === "permanentZipCode") {
      const digits = value.replace(/\D/g, "");

      if (digits.length <= 10) {
        setFormData((prev) => ({ ...prev, [name]: digits }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }

      return;
    }

    if (name === "currentCountry") {
      setFormData((prev) => ({
        ...prev,
        currentCountry: value,
        customCountry: value === "Other" ? prev.customCountry : "",
        currentState: value === "India" ? prev.currentState : ""
      }));
      setErrors((prev) => ({
        ...prev,
        currentCountry: "",
        customCountry: "",
        currentState: ""
      }));
      return;
    }

    if (name === "skills") {
      setFormData((prev) => ({ ...prev, skills: Array.isArray(value) ? value : [] }));
      setErrors((prev) => ({ ...prev, skills: "" }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const newErrors = {};
    const builtFullName = [formData.firstName, formData.middleName, formData.lastName]
      .filter(Boolean)
      .join(" ")
      .trim();
    const primaryEducation = educationEntries[0] || createEducationEntry();

    if (!formData.title) newErrors.title = "Title required";

    if (!formData.dateOfBirth) newErrors.dateOfBirth = "Date Of Birth required";

    if (!formData.firstName) newErrors.firstName = "First Name required";
    else if (formData.firstName.length < 2) newErrors.firstName = "Minimum 2 characters required";

    if (!formData.lastName) newErrors.lastName = "Last Name required";

    if (!builtFullName) newErrors.fullName = "Full Name required";
    else if (builtFullName.length < 2) newErrors.fullName = "Minimum 2 characters required";

    if (!formData.email) newErrors.email = "Email required";
    else if (!/^\S+@\S+\.\S+$/.test(formData.email)) newErrors.email = "Invalid email address";
    else if (
      candidateEmail &&
      formData.email.trim().toLowerCase() !== candidateEmail.trim().toLowerCase()
    ) {
      newErrors.email = "Email must match your candidate profile email";
    }

    if (!formData.phone) newErrors.phone = "Phone required";
    else if (formData.phone.length !== 10) newErrors.phone = "Phone must be 10 digits";

    // if (!formData.employeeId.trim()) newErrors.employeeId = "Referral ID required";
    if (!formData.referred) newErrors.referred = "Referred required";
    if (formData.referred === "true" && !formData.employeeId.trim()) {
      newErrors.employeeId = "Referral ID required";
    }

    if (!formData.totalExperience) newErrors.totalExperience = "Total Experience required";

    if (!formData.currentCountry) newErrors.currentCountry = "Country of Residence required";
    if (formData.currentCountry === "Other" && !formData.customCountry.trim()) {
      newErrors.customCountry = "Country name required";
    }

    if (!formData.currentAddressLine1) {
      newErrors.currentAddressLine1 = "Address Line 1 required";
    }

    if (!formData.currentCity) newErrors.currentCity = "City required";

    if (!formData.currentState) newErrors.currentState = "State/Province/Region required";

    if (!formData.currentZipCode) newErrors.currentZipCode = "Zip Code required";

    if (!primaryEducation.isHigherEducation) {
      newErrors.isHigherEducation = "Higher education selection required";
    }

    if (!primaryEducation.qualification) newErrors.qualification = "Qualification required";

    if (!primaryEducation.specialization) newErrors.specialization = "Specialization required";

    if (!primaryEducation.educationType) newErrors.educationType = "Education Type required";

    if (!primaryEducation.collegeInstituteName) {
      newErrors.collegeInstituteName = "College/Institute Name required";
    }

    if (!primaryEducation.universityName) {
      newErrors.universityName = "University Name required";
    } else if (
      primaryEducation.universityName === "Others" &&
      !primaryEducation.customUniversityName.trim()
    ) {
      newErrors.customUniversityName = "University Name required";
    }

    if (!primaryEducation.educationFromDate) newErrors.educationFromDate = "From Date required";

    if (!primaryEducation.educationEndDate) newErrors.educationEndDate = "End Date required";

    if (!formData.skills.length) newErrors.skills = "Skills required";

    if (formData.linkedin && !/^https?:\/\/.+/.test(formData.linkedin)) {
      newErrors.linkedin = "Invalid LinkedIn URL";
    }

    if (formData.portfolio && !/^https?:\/\/.+/.test(formData.portfolio)) {
      newErrors.portfolio = "Invalid GitHub URL";
    }

    if (!formData.resume && !errors.resume) {
      newErrors.resume = "Resume is required (PDF only)";
    }

    const nextSection =
      newErrors.resume
        ? "my-documents"
        : newErrors.title ||
            newErrors.dateOfBirth ||
            newErrors.firstName ||
            newErrors.lastName ||
            newErrors.email ||
            newErrors.phone ||
            newErrors.employeeId ||
            newErrors.referred ||
            newErrors.fullName ||
            newErrors.totalExperience ||
            newErrors.currentCountry ||
            newErrors.customCountry ||
            newErrors.currentAddressLine1 ||
            newErrors.currentCity ||
            newErrors.currentState ||
            newErrors.currentZipCode
          ? "profile-information"
          : newErrors.isHigherEducation ||
              newErrors.qualification ||
              newErrors.specialization ||
              newErrors.educationType ||
              newErrors.collegeInstituteName ||
              newErrors.universityName ||
              newErrors.customUniversityName ||
              newErrors.educationFromDate ||
              newErrors.educationEndDate
            ? "education"
            : newErrors.skills || newErrors.linkedin || newErrors.portfolio
              ? "job-specific"
              : "";

    setErrors((prev) => ({
      ...newErrors,
      resume: prev.resume || newErrors.resume
    }));

    if (nextSection) {
      setOpenSection(nextSection);
      setValidationPopupMessage("Please fill all required fields before applying.");
    } else {
      setValidationPopupMessage("");
    }

    return Object.keys(newErrors).length === 0;
  };

  const handleEducationChange = (index, event) => {
    const { name, value } = event.target;

    setEducationEntries((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index
          ? {
              ...entry,
              [name]: value,
              ...(name === "universityName" && value !== "Others"
                ? { customUniversityName: "" }
                : {})
            }
          : entry
      )
    );

    if (index === 0) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
        ...(name === "universityName" ? { customUniversityName: "" } : {})
      }));
    }
  };

  const handleAddEducation = () => {
    setEducationEntries((prev) => [...prev, createEducationEntry()]);
  };

  const handleRemoveEducation = (index) => {
    setEducationEntries((prev) => prev.filter((_, entryIndex) => entryIndex !== index));
  };

  const handleCertificationChange = (index, event) => {
    const { name, value } = event.target;

    setCertificationEntries((prev) =>
      prev.map((entry, entryIndex) =>
        entryIndex === index ? { ...entry, [name]: value } : entry
      )
    );
  };

  const handleAddCertification = () => {
    setCertificationEntries((prev) => [...prev, createCertificationEntry()]);
  };

  const handleRemoveCertification = (index) => {
    setCertificationEntries((prev) => prev.filter((_, entryIndex) => entryIndex !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;
    if (errors.resume) {
      setOpenSection("my-documents");
      setValidationPopupMessage(errors.resume);
      return;
    }

    try {
      const form = new FormData();
      const fullName = [formData.firstName, formData.middleName, formData.lastName]
        .filter(Boolean)
        .join(" ")
        .trim();
      const primaryEducation = educationEntries[0] || createEducationEntry();
      const normalizedCertifications = certificationEntries.filter((entry) =>
        Object.values(entry).some((value) => value.trim() !== "")
      );
      const normalizedEducations = educationEntries
        .map((entry) => ({
          ...entry,
          universityName: normalizeUniversityName(entry)
        }))
        .filter((entry) =>
          Object.values(entry).some((value) => value.trim() !== "")
        )
        .map(({ customUniversityName, ...entry }) => entry);
      const normalizedCurrentCountry =
        formData.currentCountry === "Other"
          ? formData.customCountry.trim()
          : formData.currentCountry;
      const normalizedSkills = formData.skills.map((skill) => skill.trim()).filter(Boolean);
      const normalizedPrimaryUniversityName = normalizeUniversityName(primaryEducation);

      form.append("title", formData.title);
      form.append("firstName", formData.firstName);
      form.append("middleName", formData.middleName);
      form.append("lastName", formData.lastName);
      form.append("dateOfBirth", formData.dateOfBirth);
      form.append("fullName", fullName);
      form.append("email", formData.email);
      form.append("phone", formData.phone);
      form.append("employeeId", formData.employeeId);
      form.append("referred", formData.referred);
      form.append("secondaryPhone", formData.secondaryPhone);
      form.append("totalExperience", formData.totalExperience);
      form.append("isHigherEducation", primaryEducation.isHigherEducation);
      form.append("qualification", primaryEducation.qualification);
      form.append("specialization", primaryEducation.specialization);
      form.append("educationType", primaryEducation.educationType);
      form.append("collegeInstituteName", primaryEducation.collegeInstituteName);
      form.append("universityName", normalizedPrimaryUniversityName);
      form.append("educationFromDate", primaryEducation.educationFromDate);
      form.append("educationEndDate", primaryEducation.educationEndDate);
      form.append("currentCountry", normalizedCurrentCountry);
      form.append("currentAddressLine1", formData.currentAddressLine1);
      form.append("currentAddressLine2", formData.currentAddressLine2);
      form.append("currentCity", formData.currentCity);
      form.append("currentState", formData.currentState);
      form.append("currentZipCode", formData.currentZipCode);
      form.append("skills", normalizedSkills.join(", "));
      form.append("otherSkillSet", formData.otherSkillSet);
      form.append("linkedinUrl", formData.linkedin);
      form.append("githubUrl", formData.portfolio);
      form.append("educations", JSON.stringify(normalizedEducations));
      form.append("certifications", JSON.stringify(normalizedCertifications));
      form.append("resume", formData.resume);

      const res = await API.post(`/applications/apply/${jobId}`, form);

      if (res.data.success) {
        setFormData(createInitialFormData());
        setEducationEntries([createEducationEntry()]);
        setCertificationEntries([createCertificationEntry()]);
        setErrors({});
        setResumeSuccess("");
        setOpenSection("my-documents");
        setShowPreview(false);
        setSubmitted(true);
        setStatus(res.data.data.status);
        setSubmitMessage("");
        setValidationPopupMessage("");
        setShowSuccessModal(true);
      }
    } catch (error) {
      const message = error.response?.data?.message || "Application failed";
      const isReferralIdError = /referral employee id|referral id|employee id/i.test(
        message,
      );

      setErrors((prev) => ({
        ...prev,
        employeeId: isReferralIdError ? message : "",
        resume: isReferralIdError ? "" : message,
      }));

      setOpenSection(
        isReferralIdError ? "profile-information" : "my-documents",
      );
      setSubmitMessage(message);
    }
  };

  const toggleSection = (sectionId) => {
    setOpenSection((prev) => (prev === sectionId ? "" : sectionId));
  };

  const handlePreview = () => {
    setShowPreview(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleBackToApplication = () => {
    setShowPreview(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const fullName = [formData.firstName, formData.middleName, formData.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const currentCountry =
    formData.currentCountry === "Other" ? formData.customCountry : formData.currentCountry;
  const previewSections = [
    {
      title: "Documents",
      rows: [
        ["Resume", displayValue(formData.resume)],
        [
          "Certifications",
          certificationEntries.map((entry, index) => ({
            title: `Certification ${index + 1}`,
            rows: [
              ["Certification/License Name", displayValue(entry.certificationName)],
              ["Institution", displayValue(entry.institution)],
              ["Effective Date", displayValue(entry.effectiveDate)],
              ["Expiration Date", displayValue(entry.expirationDate)]
            ]
          }))
        ]
      ]
    },
    {
      title: "Profile Information",
      rows: [
        ["Title", displayValue(formData.title)],
        ["Full Name", displayValue(fullName)],
        ["Date of Birth", displayValue(formData.dateOfBirth)],
        ["Primary Phone Number", displayValue(formData.phone)],
        ["Primary Email ID", displayValue(formData.email)],
        ["Referral ID", displayValue(formData.employeeId)],
        ["Referred", displayValue(formData.referred)],
        ["Secondary Phone", displayValue(formData.secondaryPhone)],
        ["Total Experience", displayValue(formData.totalExperience)],
        ["Country of Residence", displayValue(currentCountry)],
        ["Address Line 1", displayValue(formData.currentAddressLine1)],
        ["Address Line 2", displayValue(formData.currentAddressLine2)],
        ["City", displayValue(formData.currentCity)],
        ["State/Province/Region", displayValue(formData.currentState)],
        ["Zip Code", displayValue(formData.currentZipCode)]
      ]
    },
    {
      title: "Education",
      rows: educationEntries.map((entry, index) => ({
        title: `Education ${index + 1}`,
        rows: [
          ["Is this your higher education?", displayValue(entry.isHigherEducation)],
          ["Qualification", displayValue(entry.qualification)],
          ["Specialization", displayValue(entry.specialization)],
          ["Education Type", displayValue(entry.educationType)],
          ["College/Institute Name", displayValue(entry.collegeInstituteName)],
          ["University Name", displayValue(normalizeUniversityName(entry))],
          ["From Date", displayValue(entry.educationFromDate)],
          ["End Date", displayValue(entry.educationEndDate)]
        ]
      }))
    },
    {
      title: "Job-Specific Information",
      rows: [
        ["Skills", displayValue(formData.skills)],
        ["Other Skills", displayValue(formData.otherSkillSet)],
        ["LinkedIn", displayValue(formData.linkedin)],
        ["GitHub / Portfolio", displayValue(formData.portfolio)]
      ]
    }
  ];

  const renderPreviewRows = (rows) =>
    rows.map((row) => {
      if (!Array.isArray(row)) {
        return (
          <div key={row.title} className="apply-preview-subsection">
            <h4>{row.title}</h4>
            <div className="apply-preview-grid">{renderPreviewRows(row.rows)}</div>
          </div>
        );
      }

      const [label, value] = row;

      if (Array.isArray(value)) {
        return value.length ? (
          <div key={label} className="apply-preview-nested">
            <h4>{label}</h4>
            {value.map((item) => (
              <div key={item.title} className="apply-preview-subsection">
                <h4>{item.title}</h4>
                <div className="apply-preview-grid">{renderPreviewRows(item.rows)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div key={label} className="apply-preview-item">
            <span className="apply-preview-label">{label}</span>
            <span className="apply-preview-value">Not provided</span>
          </div>
        );
      }

      return (
        <div key={label} className="apply-preview-item">
          <span className="apply-preview-label">{label}</span>
          <span className="apply-preview-value">{value}</span>
        </div>
      );
    });

  const sections = [
    {
      id: "my-documents",
      title: "My Documents",
      content: (
        <div className="apply-documents-panel">
          <p className="apply-documents-note">
            Accepted file types: DOCX, PDF, Image and Text (MSG, PPT and XLS file
            types are not accepted for resume or cover letters)
          </p>

          <div className="apply-document-row">
            <div className="apply-document-header">
              <label htmlFor="resume" className="apply-document-label">
                <RequiredMark />
                Resume
              </label>
              <span className="apply-document-help" aria-hidden="true">
                ?
              </span>
            </div>

            <label htmlFor="resume" className="apply-upload-card">
              <span className="apply-upload-card-body">
                {formData.resume ? "Change Resume" : "Upload a Resume"}
              </span>
              <span className="apply-upload-card-footer">+</span>
            </label>

            <input
              id="resume"
              className="apply-upload-input"
              type="file"
              name="resume"
              accept=".pdf"
              onChange={handleChange}
            />

            <span className="apply-hint">PDF only, max size 5MB</span>
            <span className={errors.resume ? "error" : "resume-success"}>
              {errors.resume ? errors.resume : resumeSuccess}
            </span>
          </div>

          <div className="apply-education-panel">
            {certificationEntries.map((entry, index) => (
              <div key={`certification-${index}`} className="apply-education-entry">
                {index > 0 && (
                  <div className="apply-education-entry-head">
                    <button
                      type="button"
                      className="apply-education-remove"
                      onClick={() => handleRemoveCertification(index)}
                    >
                      Remove
                    </button>
                  </div>
                )}

                <div className="apply-education-grid">
                  <div className="apply-education-field">
                    <label
                      htmlFor={`certificationName-${index}`}
                      className="apply-education-label"
                    >
                      Certification/License Name
                    </label>
                    <input
                      id={`certificationName-${index}`}
                      name="certificationName"
                      value={entry.certificationName}
                      onChange={(event) => handleCertificationChange(index, event)}
                    />
                  </div>

                  <div className="apply-education-field">
                    <label htmlFor={`institution-${index}`} className="apply-education-label">
                      Institution
                    </label>
                    <input
                      id={`institution-${index}`}
                      name="institution"
                      value={entry.institution}
                      onChange={(event) => handleCertificationChange(index, event)}
                    />
                  </div>

                  <div className="apply-education-field">
                    <label htmlFor={`effectiveDate-${index}`} className="apply-education-label">
                      Effective Date
                    </label>
                    <DatePicker
                      id={`effectiveDate-${index}`}
                      selected={parseDateValue(entry.effectiveDate)}
                      onChange={(date) =>
                        handleCertificationChange(index, {
                          target: {
                            name: "effectiveDate",
                            value: formatDateValue(date)
                          }
                        })
                      }
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-mm-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      className="apply-datepicker-input"
                      wrapperClassName="apply-datepicker-wrapper"
                      popperClassName="apply-datepicker-popper"
                    />
                  </div>

                  <div className="apply-education-field">
                    <label htmlFor={`expirationDate-${index}`} className="apply-education-label">
                      Expiration Date
                    </label>
                    <DatePicker
                      id={`expirationDate-${index}`}
                      selected={parseDateValue(entry.expirationDate)}
                      onChange={(date) =>
                        handleCertificationChange(index, {
                          target: {
                            name: "expirationDate",
                            value: formatDateValue(date)
                          }
                        })
                      }
                      dateFormat="dd-MM-yyyy"
                      placeholderText="dd-mm-yyyy"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                      yearDropdownItemNumber={100}
                      scrollableYearDropdown
                      className="apply-datepicker-input"
                      wrapperClassName="apply-datepicker-wrapper"
                      popperClassName="apply-datepicker-popper"
                    />
                  </div>
                </div>
              </div>
            ))}

            <button
              type="button"
              className="apply-education-add"
              onClick={handleAddCertification}
            >
              + Add
            </button>
          </div>
        </div>
      )
    },
    {
      id: "profile-information",
      title: "Profile Information",
      content: (
        <div className="apply-profile-panel">
          <p className="apply-profile-note">Please complete your personal information.</p>

          <div className="apply-profile-grid">
            <div className="apply-profile-field">
              <label htmlFor="title" className="apply-profile-label ">
                <RequiredMark />
                Title
              </label>
              <select id="title" name="title" value={formData.title} onChange={handleChange}>
                <option value="">No Selection</option>
                <option value="Mr">Mr</option>
                <option value="Ms">Ms</option>
                <option value="Mrs">Mrs</option>
                <option value="Dr">Dr</option>
              </select>
              <span className="error">{errors.title}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="firstName" className="apply-profile-label">
                <RequiredMark />
                First Name
              </label>
              <input
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
              />
              <span className="error">{errors.firstName}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="middleName" className="apply-profile-label">
                Middle Name
              </label>
              <input
                id="middleName"
                name="middleName"
                value={formData.middleName}
                onChange={handleChange}
              />
              <span className="error">{errors.middleName}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="lastName" className="apply-profile-label">
                <RequiredMark />
                Last Name
              </label>
              <input
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
              />
              <span className="error">{errors.lastName}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="dateOfBirth" className="apply-profile-label">
                <RequiredMark />
                Date of birth
              </label>
              <DatePicker
                id="dateOfBirth"
                selected={parseDateValue(formData.dateOfBirth)}
                onChange={(date) => {
                  setFormData((prev) => ({
                    ...prev,
                    dateOfBirth: formatDateValue(date)
                  }));
                  setErrors((prev) => ({ ...prev, dateOfBirth: "" }));
                }}
                dateFormat="dd-MM-yyyy"
                placeholderText="dd-mm-yyyy"
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
                maxDate={new Date()}
                yearDropdownItemNumber={100}
                scrollableYearDropdown
                className="apply-datepicker-input"
                wrapperClassName="apply-datepicker-wrapper"
                popperClassName="apply-datepicker-popper"
              />
              <span className="error">{errors.dateOfBirth}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="phone" className="apply-profile-label">
                <RequiredMark />
                Primary Phone Number
              </label>
              <input id="phone" name="phone" value={formData.phone} onChange={handleChange} />
              <span className="error">{errors.phone}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="email" className="apply-profile-label">
                <RequiredMark />
                Primary Email ID
              </label>
              <input
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                readOnly
              />
              <span className="error">{errors.email}</span>
            </div>

            <div className="apply-profile-field">
              <div className="apply-label-with-help">
                <label htmlFor="employeeId" className="apply-profile-label">
                  Referral ID
                </label>
                <button
                  type="button"
                  className="apply-label-info-btn"
                  aria-label="Referral ID information"
                  aria-expanded={showReferralInfo}
                  onClick={() => setShowReferralInfo((prev) => !prev)}
                >
                  i
                </button>
              </div>
              {showReferralInfo && (
                <div className="apply-referral-info-card">
                  IF YOU HAVE VALID REFERRAL ID ONLY PLEASE FILL THIS FIELD
                  ELSE PLEASE LEAVE IT BLANK OTHERWISE YOUR APPLICATION WONT
                  BE SUBMITTED SUCCESSFULLY
                </div>
              )}
              <input
                id="employeeId"
                name="employeeId"
                value={formData.employeeId}
                onChange={handleChange}
                placeholder="Referral ID"
              />
              <span className="error">{errors.employeeId}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="referred" className="apply-profile-label">
                <RequiredMark />
                Referred
              </label>
              <select
                id="referred"
                name="referred"
                value={formData.referred}
                onChange={handleChange}
              >
                <option value="">No Selection</option>
                <option value="true">true</option>
                <option value="false">false</option>
              </select>
              <span className="error">{errors.referred}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="secondaryPhone" className="apply-profile-label">
                Secondary Phone
              </label>
              <input
                id="secondaryPhone"
                name="secondaryPhone"
                value={formData.secondaryPhone}
                onChange={handleChange}
              />
              <span className="error">{errors.secondaryPhone}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="totalExperience" className="apply-profile-label">
                <RequiredMark />
                Total Experience
              </label>
              <select
                id="totalExperience"
                name="totalExperience"
                value={formData.totalExperience}
                onChange={handleChange}
              >
                <option value="">No Selection</option>
                <option value="0">0</option>
                <option value="1">1</option>
                <option value="2">2</option>
                <option value="3">3</option>
                <option value="4">4</option>
                <option value="5+">5+</option>
              </select>
              <span className="error">{errors.totalExperience}</span>
            </div>
          </div>

          <div className="apply-diversity-block apply-address-block">
            <div className="apply-diversity-title">Address</div>
          </div>

          <div className="apply-profile-grid apply-profile-grid-section">
            <div className="apply-profile-field">
              <label htmlFor="currentCountry" className="apply-profile-label">
                <RequiredMark />
                Country of Residence:
              </label>
              <select
                id="currentCountry"
                name="currentCountry"
                value={formData.currentCountry}
                onChange={handleChange}
              >
                <option value="">No Selection</option>
                <option value="India">India</option>
                <option value="United States">United States</option>
                <option value="Other">Other</option>
              </select>
              <span className="error">{errors.currentCountry}</span>
            </div>

            {formData.currentCountry === "Other" && (
              <div className="apply-profile-field">
                <label htmlFor="customCountry" className="apply-profile-label">
                  <RequiredMark />
                  Enter Country
                </label>
                <input
                  id="customCountry"
                  name="customCountry"
                  value={formData.customCountry}
                  onChange={handleChange}
                />
                <span className="error">{errors.customCountry}</span>
              </div>
            )}

            <div className="apply-profile-field">
              <label htmlFor="currentAddressLine1" className="apply-profile-label">
                <RequiredMark />
                Address Line 1
              </label>
              <input
                id="currentAddressLine1"
                name="currentAddressLine1"
                value={formData.currentAddressLine1}
                onChange={handleChange}
              />
              <span className="error">{errors.currentAddressLine1}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="currentAddressLine2" className="apply-profile-label">
                Address Line 2
              </label>
              <input
                id="currentAddressLine2"
                name="currentAddressLine2"
                value={formData.currentAddressLine2}
                onChange={handleChange}
              />
              <span className="error">{errors.currentAddressLine2}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="currentCity" className="apply-profile-label">
                <RequiredMark />
                City
              </label>
              <input
                id="currentCity"
                name="currentCity"
                value={formData.currentCity}
                onChange={handleChange}
              />
              <span className="error">{errors.currentCity}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="currentState" className="apply-profile-label">
                <RequiredMark />
                State/Province/Region:
              </label>
              {formData.currentCountry === "India" ? (
                <select
                  id="currentState"
                  name="currentState"
                  value={formData.currentState}
                  onChange={handleChange}
                >
                  <option value="">No Selection</option>
                  {INDIA_STATES_AND_UTS.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                  <option value="Other">Other</option>
                </select>
              ) : (
                <input
                  id="currentState"
                  name="currentState"
                  value={formData.currentState}
                  onChange={handleChange}
                />
              )}
              <span className="error">{errors.currentState}</span>
            </div>

            <div className="apply-profile-field">
              <label htmlFor="currentZipCode" className="apply-profile-label">
                <RequiredMark />
                Zip Code
              </label>
              <input
                id="currentZipCode"
                name="currentZipCode"
                value={formData.currentZipCode}
                onChange={handleChange}
              />
              <span className="error">{errors.currentZipCode}</span>
            </div>
          </div>

        </div>
      )
    },
    {
      id: "education",
      title: "Education",
      content: (
        <div className="apply-education-panel">
          {educationEntries.map((entry, index) => (
            <div key={`education-${index}`} className="apply-education-entry">
              {index > 0 && (
                <div className="apply-education-entry-head">
                  <button
                    type="button"
                    className="apply-education-remove"
                    onClick={() => handleRemoveEducation(index)}
                  >
                    Remove
                  </button>
                </div>
              )}

              <div className="apply-education-grid">
                <div className="apply-education-field">
                  <label htmlFor={`isHigherEducation-${index}`} className="apply-education-label">
                    <RequiredMark />
                    Is this your higher education?
                  </label>
                  <SearchableDropdown
                    id={`isHigherEducation-${index}`}
                    name="isHigherEducation"
                    value={entry.isHigherEducation}
                    onChange={(event) => handleEducationChange(index, event)}
                    options={EDUCATION_OPTIONS.isHigherEducation}
                  />
                  <span className="error">{index === 0 ? errors.isHigherEducation : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`qualification-${index}`} className="apply-education-label">
                    <RequiredMark />
                    Qualification
                  </label>
                  <SearchableDropdown
                    id={`qualification-${index}`}
                    name="qualification"
                    value={entry.qualification}
                    onChange={(event) => handleEducationChange(index, event)}
                    options={EDUCATION_OPTIONS.qualification}
                  />
                  <span className="error">{index === 0 ? errors.qualification : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`specialization-${index}`} className="apply-education-label">
                    <RequiredMark />
                    Specialization
                  </label>
                  <SearchableDropdown
                    id={`specialization-${index}`}
                    name="specialization"
                    value={entry.specialization}
                    onChange={(event) => handleEducationChange(index, event)}
                    options={EDUCATION_OPTIONS.specialization}
                  />
                  <span className="error">{index === 0 ? errors.specialization : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`educationType-${index}`} className="apply-education-label">
                    <RequiredMark />
                    Education Type
                  </label>
                  <SearchableDropdown
                    id={`educationType-${index}`}
                    name="educationType"
                    value={entry.educationType}
                    onChange={(event) => handleEducationChange(index, event)}
                    options={EDUCATION_OPTIONS.educationType}
                  />
                  <span className="error">{index === 0 ? errors.educationType : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`collegeInstituteName-${index}`} className="apply-education-label">
                    <RequiredMark />
                    College/Institute Name
                  </label>
                  <input
                    id={`collegeInstituteName-${index}`}
                    name="collegeInstituteName"
                    value={entry.collegeInstituteName}
                    onChange={(event) => handleEducationChange(index, event)}
                  />
                  <span className="error">{index === 0 ? errors.collegeInstituteName : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`universityName-${index}`} className="apply-education-label">
                    <RequiredMark />
                    University Name
                  </label>
                  <SearchableDropdown
                    id={`universityName-${index}`}
                    name="universityName"
                    value={entry.universityName}
                    onChange={(event) => handleEducationChange(index, event)}
                    options={formOptions.universities}
                  />
                  <span className="error">{index === 0 ? errors.universityName : ""}</span>
                </div>

                {entry.universityName === "Others" && (
                  <div className="apply-education-field">
                    <label
                      htmlFor={`customUniversityName-${index}`}
                      className="apply-education-label"
                    >
                      <RequiredMark />
                      Enter University Name
                    </label>
                    <input
                      id={`customUniversityName-${index}`}
                      name="customUniversityName"
                      value={entry.customUniversityName}
                      onChange={(event) => handleEducationChange(index, event)}
                    />
                    <span className="error">
                      {index === 0 ? errors.customUniversityName : ""}
                    </span>
                  </div>
                )}

                <div className="apply-education-field">
                  <label htmlFor={`educationFromDate-${index}`} className="apply-education-label">
                    <RequiredMark />
                    From Date
                  </label>
                  <DatePicker
                    id={`educationFromDate-${index}`}
                    selected={parseDateValue(entry.educationFromDate)}
                    onChange={(date) =>
                      handleEducationChange(index, {
                        target: {
                          name: "educationFromDate",
                          value: formatDateValue(date)
                        }
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    className="apply-datepicker-input"
                    wrapperClassName="apply-datepicker-wrapper"
                    popperClassName="apply-datepicker-popper"
                  />
                  <span className="error">{index === 0 ? errors.educationFromDate : ""}</span>
                </div>

                <div className="apply-education-field">
                  <label htmlFor={`educationEndDate-${index}`} className="apply-education-label">
                    <RequiredMark />
                    End Date
                  </label>
                  <DatePicker
                    id={`educationEndDate-${index}`}
                    selected={parseDateValue(entry.educationEndDate)}
                    onChange={(date) =>
                      handleEducationChange(index, {
                        target: {
                          name: "educationEndDate",
                          value: formatDateValue(date)
                        }
                      })
                    }
                    dateFormat="dd-MM-yyyy"
                    placeholderText="dd-mm-yyyy"
                    showMonthDropdown
                    showYearDropdown
                    dropdownMode="select"
                    maxDate={new Date()}
                    yearDropdownItemNumber={100}
                    scrollableYearDropdown
                    className="apply-datepicker-input"
                    wrapperClassName="apply-datepicker-wrapper"
                    popperClassName="apply-datepicker-popper"
                  />
                  <span className="error">{index === 0 ? errors.educationEndDate : ""}</span>
                </div>
              </div>
            </div>
          ))}

          <button type="button" className="apply-education-add" onClick={handleAddEducation}>
            + Add
          </button>
        </div>
      )
    },
    {
      id: "job-specific",
      title: "Job-Specific Information",
      content: (
          <div className="apply-fields-grid">
            <div className="apply-field apply-field-full">
              <label htmlFor="skills">
                <RequiredMark />
                Skills
              </label>
              <MultiSelectDropdown
                id="skills"
                name="skills"
                value={formData.skills}
                onChange={handleChange}
                options={SKILL_OPTIONS}
                placeholder="Type or select skills"
              />
              <span className="error">{errors.skills}</span>
            </div>
            <div className="apply-field apply-field-full">
              <label htmlFor="otherSkillSet">Skills(If Other, mention the skillset below)</label>
              <input
                id="otherSkillSet"
                name="otherSkillSet"
                value={formData.otherSkillSet}
                onChange={handleChange}
              />
              <span className="error">{errors.otherSkillSet}</span>
            </div>
            <div className="apply-field apply-field-full">
              <label htmlFor="portfolio">GitHub / Portfolio</label>
              <input id="portfolio" name="portfolio" value={formData.portfolio} onChange={handleChange} />
            <span className="error">{errors.portfolio}</span>
          </div>
        </div>
      )
    }
  ];

  return (
    <div className="apply-wrapper">
      <div className="apply-layout">
        {!showPreview && (
          <div className="apply-topbar">
            <Link to={backJobPath} className="back-link">
              Back to Job Details
            </Link>
          </div>
        )}

        <div className="apply-container">
          {submitMessage && !submitted && <p className="apply-status-error">{submitMessage}</p>}

          {submitted && (
            <p className="apply-status-success">Application Submitted. Status: {status}</p>
          )}

          <form onSubmit={handleSubmit} noValidate>
            {showPreview ? (
              <div className="apply-preview">
                <div className="apply-preview-header">
                  <div>
                    <h2>Application Preview</h2>
                    <p>Please check your details before submitting.</p>
                  </div>
                </div>

                {previewSections.map((section) => (
                  <section key={section.title} className="apply-preview-section">
                    <h3>{section.title}</h3>
                    <div className="apply-preview-grid">{renderPreviewRows(section.rows)}</div>
                  </section>
                ))}
              </div>
            ) : (
              <div className="apply-accordion">
                {sections.map((section) => {
                  const isOpen = openSection === section.id;

                  return (
                    <section key={section.id} className="apply-accordion-item">
                      <button
                        type="button"
                        className={`apply-accordion-trigger${isOpen ? " open" : ""}`}
                        onClick={() => toggleSection(section.id)}
                        aria-expanded={isOpen}
                      >
                        <span className="apply-accordion-icon" aria-hidden="true">
                          {isOpen ? "v" : ">"}
                        </span>
                        <span>{section.title}</span>
                      </button>

                      {isOpen && <div className="apply-accordion-panel">{section.content}</div>}
                    </section>
                  );
                })}
              </div>
            )}

            <div className="apply-actions">
              {showPreview ? (
                <button
                  type="button"
                  className="apply-action-btn apply-action-left"
                  onClick={handleBackToApplication}
                >
                  Back to Application
                </button>
              ) : (
                <button
                  type="button"
                  className="apply-action-btn apply-action-left"
                  onClick={handlePreview}
                >
                  Preview
                </button>
              )}

              <div className="apply-action-group">
                <button type="submit" className="apply-action-btn" disabled={submitted}>
                  {submitted ? "Applied" : "Apply"}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {showSuccessModal && (
        <div className="apply-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="apply-modal" onClick={(event) => event.stopPropagation()}>
            <p className="apply-modal-title">Job applied successfully</p>
            <p className="apply-modal-text">Your application has been submitted.</p>
            <button
              type="button"
              className="apply-modal-btn"
              onClick={() => setShowSuccessModal(false)}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {validationPopupMessage && (
        <div
          className="apply-modal-overlay"
          onClick={() => setValidationPopupMessage("")}
        >
          <div className="apply-modal" onClick={(event) => event.stopPropagation()}>
            <p className="apply-modal-title">Application incomplete</p>
            <p className="apply-modal-text">{validationPopupMessage}</p>
            <button
              type="button"
              className="apply-modal-btn"
              onClick={() => setValidationPopupMessage("")}
            >
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplyPage;
